import { useMutation, useQueries } from '@tanstack/react-query';
import { APITypes, MedicalDataTypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import { appAPI } from '../../utils/api';
import { getCareTeamMemberResourcesCacheKey, onResourceRelatedCareTeamMemberUpdates } from './cacheKeys';
import { PrimaryRecordResource } from '../../primary-record-types/MedicalDataTypes';
import { getIntl } from '../../utils/intl';
import useNotify from '../useNotify';

const intl = getIntl('useCareTeamMemberResources', {
    updateFailure: 'Failed to update care team member resources',
});

export type UseCareTeamMemberResourcesArgs = {
    medicalProfileId: string;
    careTeamMemberId: string;
    resourceCategories: MedicalDataTypes.PrimaryRecordResourceCategory[];
    onUpdateCareTeamMemberResourcesSuccess?: () => void;
};

type CareTeamMemberResources = Record<
    MedicalDataTypes.PrimaryRecordResourceCategory,
    APITypes.medicalDataService.ListResourceResponseItem<PrimaryRecordResource>[]
>;

export type UseCareTeamMemberResourcesReturnValue = {
    resources: CareTeamMemberResources;
    isLoadingResources: boolean;
    updateCareTeamMemberResources: ({
        resourceCategory,
        resourceIds,
    }: {
        resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
        resourceIds: string[];
    }) => void;
    isUpdatingCareTeamMemberResources: boolean;
};

export default function useCareTeamMemberResources({
    medicalProfileId,
    careTeamMemberId,
    resourceCategories,
    onUpdateCareTeamMemberResourcesSuccess,
}: UseCareTeamMemberResourcesArgs): UseCareTeamMemberResourcesReturnValue {
    const notify = useNotify();

    const relatedResourcesQueries = useQueries({
        queries: resourceCategories.map((resourceCategory) => ({
            queryKey: getCareTeamMemberResourcesCacheKey(medicalProfileId, careTeamMemberId, resourceCategory),
            queryFn: async () => {
                return await appAPI.get<APITypes.medicalDataService.ListResourcesResponse>(
                    urls.api.medicalData.careTeamMemberResources(medicalProfileId, careTeamMemberId, resourceCategory),
                );
            },
        })),
    });
    // TODO: add notify here for errors?
    // Would need to create a new hook to handle multiple queries

    const updateCareTeamMemberResources = useMutation({
        mutationFn: async ({
            resourceCategory,
            resourceIds,
        }: {
            resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
            resourceIds: string[];
        }) => {
            return await appAPI.put<void>(
                urls.api.medicalData.careTeamMemberResources(medicalProfileId, careTeamMemberId, resourceCategory),
                {
                    body: {
                        resourceIds: resourceIds,
                    },
                },
            );
        },
        onSuccess: (_data, { resourceCategory }) => {
            onUpdateCareTeamMemberResourcesSuccess?.();
            onResourceRelatedCareTeamMemberUpdates.resetCache({
                medicalProfileId,
                resourceCategory,
            })();
        },
        onError: () => {
            notify('error', intl('updateFailure'));
        },
    });

    return {
        resources: relatedResourcesQueries.reduce((acc, query, index) => {
            const resourceCategory = resourceCategories[index];
            if (query.isLoading) {
                return {
                    ...acc,
                    [resourceCategory]: [],
                };
            }
            return {
                ...acc,
                [resourceCategory]: query.data?.resources ?? [],
            };
        }, {} as CareTeamMemberResources),
        isLoadingResources: relatedResourcesQueries.some((query) => query.isLoading),
        updateCareTeamMemberResources: updateCareTeamMemberResources.mutate,
        isUpdatingCareTeamMemberResources: updateCareTeamMemberResources.isPending,
    };
}
